import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaUserMd, FaArrowLeft, FaHospital, FaPhone, FaEnvelope, FaToggleOn, FaToggleOff, FaBan } from 'react-icons/fa';
import { showSuccessToast, showErrorToast } from '../../../shared/utils/toastHelper';
import { useTitle } from '../../../context/TitleContext';
import EnhancedPagination from '../../../shared/components/EnhancedPagination';
import axios from 'axios';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const AdminClinicDoctors = () => {
    const { clinicId } = useParams();
    const { setTitle } = useTitle();
    const navigate = useNavigate();
    const [doctors, setDoctors] = useState([]);
    const [clinic, setClinic] = useState(null);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);

    useEffect(() => {
        setTitle('أطباء العيادة');
    }, [setTitle]);

    useEffect(() => {
        if (clinicId) {
            fetchClinicDoctors();
        }
    }, [clinicId, currentPage]);

    const getAuthToken = () => {
        const adminData = sessionStorage.getItem('adminkhadamat');
        if (adminData) {
            try {
                const parsed = JSON.parse(adminData);
                return parsed.data?.token?.access_token || parsed.data?.access_token;
            } catch {
                return null;
            }
        }
        return null;
    };

    const fetchClinicDoctors = async () => {
        try {
            setLoading(true);
            const token = getAuthToken();
            
            if (!token) {
                throw new Error('لا يوجد token للمصادقة');
            }

            const response = await axios.get(`${BASE_URL}/clinics/doctors/${clinicId}?page=${currentPage}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept-Language': 'ar',
                    'lang': 'ar',
                },
            });

            if (response.data.status === 'success' || response.data.statusCode === 200) {
                setDoctors(response.data.result?.data || []);
                setClinic(response.data.result?.clinic || null);
                setPagination(response.data.result?.pagination || null);
            } else {
                throw new Error(response.data.message || 'فشل في جلب أطباء العيادة');
            }
        } catch (error) {
            showErrorToast(error.response?.data?.message || error.message || 'فشل في جلب أطباء العيادة');
        } finally {
            setLoading(false);
        }
    };

    const handleStatusChange = async (doctorId, currentStatus) => {
        try {
            const token = getAuthToken();
            if (!token) {
                showErrorToast("لا يوجد token للمصادقة");
                return;
            }

            const newStatus = currentStatus === "1" ? "0" : "1";

            await axios.post(
                `${BASE_URL}/doctors/change_status/${doctorId}`,
                {
                    status: newStatus,
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Accept-Language": "ar",
                        lang: "ar",
                        "Content-Type": "application/json",
                    },
                }
            );

            showSuccessToast("تم تغيير حالة الطبيب بنجاح");
            fetchClinicDoctors(); // إعادة تحميل القائمة
        } catch (error) {
            showErrorToast("فشل في تغيير حالة الطبيب");
        }
    };

    const handleBanChange = async (doctorId, currentBanStatus) => {
        try {
            const token = getAuthToken();
            if (!token) {
                showErrorToast("لا يوجد token للمصادقة");
                return;
            }

            const newBanStatus = currentBanStatus === "1" ? "0" : "1";

            await axios.post(
                `${BASE_URL}/doctors/change_ban/${doctorId}?status=${newBanStatus}`,
                {},
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Accept-Language": "ar",
                        lang: "ar",
                    },
                }
            );

            showSuccessToast("تم تغيير حالة الحظر بنجاح");
            fetchClinicDoctors(); // إعادة تحميل القائمة
        } catch (error) {
            showErrorToast("فشل في تغيير حالة الحظر");
        }
    };

    const LoadingSpinner = () => (
        <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Loading...</span>
        </div>
    );

    if (loading) {
        return (
            <div className="main_content">
                <div className="dashboard-container content-wrapper">
                    <div className="text-center py-5">
                        <LoadingSpinner />
                        <p className="mt-3 text-muted">جاري تحميل أطباء العيادة...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="main_content">
            <div className="dashboard-container content-wrapper">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                        <h2 className="mb-0" style={{ fontSize: '20px' }}>
                            <FaUserMd className="mx-2" style={{ color: '#184194' }} />
                            أطباء العيادة
                        </h2>
                        {clinic && (
                            <p className="text-muted mb-0 mt-2">
                                <FaHospital className="mx-1" />
                                {clinic.name}
                            </p>
                        )}
                    </div>
                    <div>
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="btn btn-outline-secondary"
                            onClick={() => navigate('/admin-clinics')}
                            style={{ borderRadius: '8px' }}
                        >
                            <FaArrowLeft className="mx-2" />
                            العودة للعيادات
                        </motion.button>
                    </div>
                </div>

                {/* Doctors Table */}
                {doctors.length > 0 ? (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="card shadow-lg border-0"
                    >
                        <div className="card-body">
                            <div className="table-responsive">
                                <table className="table table-modern">
                                    <thead>
                                        <tr>
                                            <th className="text-center">#</th>
                                            <th className="text-center">اسم الطبيب</th>
                                            <th className="text-center">التخصص</th>
                                            <th className="text-center">الهاتف</th>
                                            <th className="text-center">التصنيف الفرعي</th>
                                            <th className="text-center">التقييم</th>
                                            <th className="text-center">الحالة</th>
                                            <th className="text-center">الحظر</th>
                                            <th className="text-center">الإجراءات</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {doctors.map((doctor, index) => (
                                            <tr key={doctor.id} className="table-row-modern">
                                                <td className="text-center">
                                                    <div className="order-number-badge">
                                                        #{(pagination?.current_page - 1) * (pagination?.per_page || 10) + index + 1}
                                                    </div>
                                                </td>
                                                <td className="text-center">
                                                    <div className="doctor-name">
                                                        {doctor.name || 'طبيب بدون اسم'}
                                                    </div>
                                                </td>
                                                <td className="text-center">
                                                    <div className="specialization-display">
                                                        {doctor.description || 'غير محدد'}
                                                    </div>
                                                </td>
                                                <td className="text-center">
                                                    <div className="phone-badge">
                                                        {doctor.phone || 'غير محدد'}
                                                    </div>
                                                </td>
                                                <td className="text-center">
                                                    <div className="category-display">
                                                        {doctor.subcategory || 'غير محدد'}
                                                    </div>
                                                </td>
                                                <td className="text-center">
                                                    <div className="rating-display">
                                                        {doctor.rate_avg && doctor.rate_avg > 0 ? (
                                                            <span>
                                                                {doctor.rate_avg} ⭐ ({doctor.rate_count})
                                                            </span>
                                                        ) : (
                                                            <span className="text-muted">لا يوجد تقييم</span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="text-center">
                                                    {doctor.status === "1" ? 
                                                        <span className="badge bg-success text-white">نشط</span> :
                                                        <span className="badge bg-danger text-white">غير نشط</span>
                                                    }
                                                </td>
                                                <td className="text-center">
                                                    {doctor.ban === "1" ? 
                                                        <span className="badge bg-danger text-white">محظور</span> :
                                                        <span className="badge bg-success text-white">غير محظور</span>
                                                    }
                                                </td>
                                                <td className="text-center">
                                                    <div className="d-flex gap-2 justify-content-center">
                                                        <button
                                                            className="btn btn-sm btn-toggle-small"
                                                            title={
                                                                doctor.status === "1"
                                                                    ? "إلغاء التفعيل"
                                                                    : "تفعيل"
                                                            }
                                                            onClick={() =>
                                                                handleStatusChange(doctor.id, doctor.status)
                                                            }
                                                        >
                                                            {doctor.status === "1" ? (
                                                                <FaToggleOff />
                                                            ) : (
                                                                <FaToggleOn />
                                                            )}
                                                        </button>
                                                        <button
                                                            className="btn btn-sm btn-ban"
                                                            title={doctor.ban === "1" ? "إلغاء الحظر" : "حظر"}
                                                            onClick={() =>
                                                                handleBanChange(doctor.id, doctor.ban)
                                                            }
                                                        >
                                                            <FaBan />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </motion.div>
                ) : (
                    <div className="text-center py-5">
                        <div className="alert alert-info">
                            <h4>لا يوجد أطباء</h4>
                            <p>لا يوجد أطباء مسجلين في هذه العيادة.</p>
                        </div>
                    </div>
                )}
                
                {/* Pagination */}
                {pagination && doctors.length > 0 && (
                    <div className="mt-4">
                        <EnhancedPagination
                            currentPage={pagination.current_page || currentPage}
                            totalPages={pagination.pages_count || 1}
                            onPageChange={setCurrentPage}
                            totalItems={pagination.total || 0}
                            itemsPerPage={pagination.per_page || 10}
                            showInfo={true}
                            className="mt-4"
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminClinicDoctors;
